import { useEffect, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import { cambioEstadoService } from '../services/cambioEstadoService';
import { tipoEstado } from '../utils/flujoEstados';

/**
 * Banner de cierre para los módulos previsionales simples: se muestra arriba de la ficha
 * cuando el trámite terminó en 'Cerrado favorable' (verde), 'Cerrado desfavorable' (rojo) o
 * 'Cancelado' (gris, V208). El cancelado va aparte del desfavorable porque significan cosas
 * distintas: desfavorable es que el organismo lo denegó; cancelado, que el trámite no siguió.
 *
 * Calca el patrón que ART y RTI ya usan (renderEstadoFinalBanner), pero resuelve el tipo por
 * nombre de estado en vez de por id hardcodeado.
 *
 * El detalle sale del comentario del cambio de estado abierto: los DTOs de estos módulos no
 * exponen un `ultimoComentario` como el de ART, así que hay que ir al historial. El fetch se
 * dispara solo cuando el banner corresponde, para no pegarle al endpoint en cada ficha abierta.
 */
function BannerEstadoFinal({ idTramite, nombreEstadoActual }) {
    const [comentario, setComentario] = useState(null);

    const tipo = tipoEstado(nombreEstadoActual);
    const esCierre = tipo === 'favorable' || tipo === 'desfavorable' || tipo === 'cancelado';

    useEffect(() => {
        if (!esCierre || !idTramite) return;
        let cancelado = false;
        (async () => {
            try {
                const historial = await cambioEstadoService.getHistorialEstados(idTramite);
                if (cancelado) return;
                const abierto = (Array.isArray(historial) ? historial : []).find((c) => !c.fechaHoraFin);
                setComentario(abierto?.comentario || null);
            } catch (error) {
                // El banner es informativo: si el historial falla igual se muestra el cierre,
                // solo que sin el detalle.
                if (!cancelado) setComentario(null);
            }
        })();
        return () => { cancelado = true; };
    }, [esCierre, idTramite, nombreEstadoActual]);

    if (!esCierre) return null;

    const CONFIG_POR_TIPO = {
        favorable: { bg: 'success', icon: <CheckCircleIcon style={{ fontSize: '2.5rem' }} />, titulo: 'Trámite cerrado favorable' },
        desfavorable: { bg: 'danger', icon: <CancelIcon style={{ fontSize: '2.5rem' }} />, titulo: 'Trámite cerrado desfavorable' },
        cancelado: { bg: 'secondary', icon: <BlockIcon style={{ fontSize: '2.5rem' }} />, titulo: 'Trámite cancelado' },
    };
    const config = CONFIG_POR_TIPO[tipo];

    return (
        <div className={`alert alert-${config.bg} border-0 rounded-4 shadow-lg p-4 mb-4 d-flex align-items-center`}>
            <span className="me-3">{config.icon}</span>
            <div>
                <h4 className="fw-bold mb-1">{config.titulo}</h4>
                <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                    <strong>Detalle:</strong> {comentario || 'Sin observaciones adicionales.'}
                </p>
            </div>
        </div>
    );
}

export default BannerEstadoFinal;
